import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { inr, inrShort, pct, ragColor } from "@/lib/format";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell,
} from "recharts";
import {
  TrendingUp, Wallet, ShieldCheck, AlertTriangle, Banknote, PieChart, Landmark, Sparkles, Building2,
} from "lucide-react";

const CHART_BG = "#1E293B";

const kpiMeta = {
  revenue: { label: "Revenue (period)", icon: TrendingUp, fmt: inrShort },
  net_cash_flow: { label: "Net Cash Flow", icon: Banknote, fmt: inrShort },
  cash_position: { label: "Cash Position", icon: Wallet, fmt: inrShort },
  tax_compliance_rate: { label: "Tax Compliance", icon: ShieldCheck, fmt: (v) => pct(v) },
  over_budget_categories: { label: "Over-Budget Categories", icon: AlertTriangle, fmt: (v) => v },
  avg_dept_profitability: { label: "Avg Profitability", icon: PieChart, fmt: (v) => pct(v) },
  fund_applications: { label: "Funds In Progress", icon: Landmark, fmt: (v) => v },
};

const ChartTooltip = ({ active, payload, label, money }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-[#334155] bg-[#1E293B] px-3 py-2 text-xs shadow-xl">
      <div className="text-slate-300 font-medium mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-400">{p.name}:</span>
          <span className="text-slate-100 tabular-nums">{money ? inr(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

const Card = ({ children, className = "", testid }) => (
  <div data-testid={testid} className={`rounded-xl border border-[#334155] bg-[#1E293B] p-6 transition-all duration-200 hover:border-[#475569] ${className}`}>
    {children}
  </div>
);

export default function Dashboard() {
  const { period } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dashboard?period=${period}`);
      setData(res.data);
    } catch (e) {}
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="flex items-center justify-center py-32"><div className="h-10 w-10 rounded-full border-2 border-[#C9A24B] border-t-transparent animate-spin" /></div>;
  }

  const insightStyle = { alert: "border-rose-600/30 bg-rose-600/8", warning: "border-amber-500/30 bg-amber-500/8", positive: "border-emerald-600/30 bg-emerald-600/8" };
  const insightDot = { alert: "bg-rose-500", warning: "bg-amber-400", positive: "bg-emerald-500" };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Finance Dashboard</h1>
        <p className="text-slate-400 text-sm mt-1">Provide financial strength, control costs, ensure compliance and support growth — auto-tracked across every module.</p>
      </div>

      {/* Focus areas */}
      <div className="flex flex-wrap gap-2">
        {["Fund Arrangements", "Balance Sheet Monitoring", "Budgeting Discipline", "Service-Wise Visibility", "Taxation Compliance", "Govt Scheme Utilization"].map((f) => (
          <span key={f} className="text-xs px-3 py-1.5 rounded-full border border-[#334155] bg-[#0F172A] text-slate-300">{f}</span>
        ))}
      </div>

      {/* KPI scorecard */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(kpiMeta).map(([k, meta]) => {
          const sc = data.scorecard.find((s) => s.metric === k);
          const val = data.kpis[k];
          const Icon = meta.icon;
          return (
            <Card key={k} testid={`kpi-${k}`}>
              <div className="flex items-start justify-between mb-3">
                <Icon className="h-5 w-5 text-[#C9A24B]" />
                {sc && <span className="h-2.5 w-2.5 rounded-full mt-1" style={{ background: ragColor(sc.rag) }} title={`RAG: ${sc.rag}`} />}
              </div>
              <div className="label-caps">{meta.label}</div>
              <div className="font-heading text-3xl font-bold tracking-tight mt-1 tabular-nums">{meta.fmt(val)}</div>
              {sc ? (
                <div className="text-xs text-slate-500 mt-1.5">Target {meta.fmt(sc.target)} · <span style={{ color: ragColor(sc.rag) }}>{sc.pct}%</span></div>
              ) : (
                <div className="text-xs text-slate-600 mt-1.5">No goal set</div>
              )}
            </Card>
          );
        })}
        <Card testid="kpi-net-flow-mini" className="flex flex-col justify-center">
          <div className="label-caps">Inflow vs Outflow</div>
          <div className="mt-2 space-y-1.5">
            <div className="flex justify-between text-sm"><span className="text-emerald-400">Inflow</span><span className="tabular-nums">{inrShort(data.inflow)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-rose-400">Outflow</span><span className="tabular-nums">{inrShort(data.outflow)}</span></div>
          </div>
        </Card>
      </div>

      {/* Centre-wise Revenue & Expense */}
      {data.center_summary && (
        <div className="space-y-4" data-testid="dashboard-center-summary">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#C9A24B]" />
            <h3 className="font-heading font-medium">Centre-wise Revenue &amp; Expense — Awish</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <Card testid="center-card-awish" className="border-[#C9A24B]/50 bg-[#C9A24B]/8">
              <div className="label-caps text-[#C9A24B]">Awish — Total</div>
              <div className="font-heading text-2xl font-bold mt-1 tabular-nums">{inrShort(data.center_summary.total_income)}</div>
              <div className="text-xs text-slate-400">total revenue</div>
              <div className="mt-2 flex justify-between text-xs">
                <span className="text-rose-400">Exp {inrShort(data.center_summary.total_expense)}</span>
                <span className={data.center_summary.net >= 0 ? "text-emerald-400" : "text-rose-400"}>Net {inrShort(data.center_summary.net)}</span>
              </div>
            </Card>
            {data.center_summary.centers.map((c) => (
              <Card key={c.center} testid={`center-card-${c.center}`}>
                <div className="label-caps truncate">{c.center}</div>
                <div className="font-heading text-xl font-bold mt-1 tabular-nums">{inrShort(c.income)}</div>
                <div className="text-xs text-slate-500">revenue</div>
                <div className="mt-2 flex justify-between text-xs">
                  <span className="text-rose-400">Exp {inrShort(c.expense)}</span>
                  <span className={c.net >= 0 ? "text-emerald-400" : "text-rose-400"}>Net {inrShort(c.net)}</span>
                </div>
              </Card>
            ))}
          </div>
          {data.center_summary.centers.some((c) => c.income || c.expense) && (
            <Card testid="chart-center">
              <h3 className="font-heading font-medium mb-4">Centre Revenue vs Expense</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.center_summary.centers} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="center" stroke="#64748B" fontSize={11} angle={-12} textAnchor="end" height={50} />
                  <YAxis stroke="#64748B" fontSize={11} tickFormatter={inrShort} />
                  <Tooltip content={<ChartTooltip money />} cursor={{ fill: "#33415533" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="income" name="Revenue" fill="#059669" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="expense" name="Expense" fill="#DC2626" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" testid="chart-budget-actual">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-medium">Budget vs Actual Spend</h3>
            <span className="text-xs text-slate-500">by category</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.budget_vs_actual} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="category" stroke="#64748B" fontSize={11} angle={-15} textAnchor="end" height={50} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={inrShort} />
              <Tooltip content={<ChartTooltip money />} cursor={{ fill: "#33415533" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="budgeted" name="Budgeted" fill="#64748B" radius={[3, 3, 0, 0]} />
              <Bar dataKey="actual" name="Actual" fill="#C9A24B" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card testid="chart-dept-profit">
          <h3 className="font-heading font-medium mb-4">Service Profitability</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.dept_profit} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis type="number" stroke="#64748B" fontSize={11} tickFormatter={inrShort} />
              <YAxis type="category" dataKey="department" stroke="#64748B" fontSize={10} width={90} />
              <Tooltip content={<ChartTooltip money />} cursor={{ fill: "#33415533" }} />
              <Bar dataKey="profit" name="Profit" radius={[0, 3, 3, 0]}>
                {data.dept_profit.map((d, i) => (
                  <Cell key={i} fill={d.profit >= 0 ? "#059669" : "#DC2626"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" testid="chart-cash-trend">
          <h3 className="font-heading font-medium mb-4">Cash Flow Trend (Inflow vs Outflow)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.cash_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={inrShort} />
              <Tooltip content={<ChartTooltip money />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="inflow" name="Inflow" stroke="#059669" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="outflow" name="Outflow" stroke="#DC2626" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="position" name="Cash Position" stroke="#C9A24B" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card testid="auto-insights" className="flex flex-col">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="h-4 w-4 text-[#C9A24B]" /><h3 className="font-heading font-medium">Auto Insights</h3></div>
          <div className="space-y-2 overflow-y-auto flex-1 max-h-[220px] pr-1">
            {data.insights.length === 0 && <p className="text-sm text-slate-500">No alerts — everything on track.</p>}
            {data.insights.map((ins, i) => (
              <div key={i} className={`flex gap-2.5 rounded-lg border px-3 py-2.5 ${insightStyle[ins.type]}`}>
                <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${insightDot[ins.type]}`} />
                <span className="text-sm text-slate-300 leading-snug">{ins.text}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Tax compliance trend */}
      <Card testid="chart-tax-trend">
        <h3 className="font-heading font-medium mb-4">Tax Filing Compliance Trend</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={data.tax_trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
            <XAxis dataKey="month" stroke="#64748B" fontSize={11} />
            <YAxis stroke="#64748B" fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip content={<ChartTooltip />} />
            <Line type="monotone" dataKey="compliance" name="Compliance %" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
